/**
 * FindPay — the universal "Pay for anything you find" surface.
 *
 * The page is a single React component that renders one of five modes
 * chosen via the top tab-strip:
 *
 *   • Home            → big-icon menu (Scan, Send, Receive, QR, Link, History)
 *   • Merchant        → merchant onboarding + dashboard
 *   • Customer        → customer dashboard (spends, refunds)
 *   • Scan            → paste UPI VPA / scan QR to pay
 *   • Test Checkout   → dev-only pay-anything drawer for admin QA
 *
 * FindPay is deliberately voice-friendly: every action has a large
 * label, an icon, and a data-testid — the wake-word AI can drive any
 * flow ("hey AI find, scan and pay ₹200 to Ravi") once we wire the
 * intent router.
 *
 * The page is feature-gated: unless REACT_APP_FINDPAY_ENABLED === 'true'
 * or the user is an admin, we render an early-access placeholder so
 * beta users don't accidentally rely on it.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Coins, QrCode, Send, HandCoins, Link2, Receipt, ShieldCheck, Store, Wallet, ScanLine, Users, Sparkles, Globe2, Building2, TrendingUp, RefreshCw, ChevronRight, ArrowUpRight, Printer, MessageCircleMore, Download } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import FindPayCheckout from "@/components/FindPayCheckout";

const TABS = [
  { id: "home",     label: "Home",      Icon: Wallet },
  { id: "scan",     label: "Scan",      Icon: ScanLine },
  { id: "merchant", label: "Merchant",  Icon: Store },
  { id: "customer", label: "History",   Icon: Receipt },
];

function paiseToStr(paise, ccy = "INR") {
  if (paise == null) return "—";
  const n = Number(paise) / 100;
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency: ccy, maximumFractionDigits: 2 }).format(n); }
  catch { return `${ccy} ${n.toFixed(2)}`; }
}

export default function FindPay() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const active = params.get("tab") || "home";
  const setTab = (t) => setParams({ tab: t });

  const enabled = (process.env.REACT_APP_FINDPAY_ENABLED === "true") || user?.is_admin;

  if (!enabled) return <EarlyAccessGate />;
  if (!user) return <SignInGate />;

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <div className="grid grid-cols-4 gap-1 bg-white rounded-2xl p-1 shadow-sm">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`findpay-tab-${t.id}`}
              className={`flex flex-col items-center py-2 rounded-xl text-xs transition ${active === t.id ? "bg-emerald-600 text-white shadow-md" : "text-stone-500 hover:bg-stone-100"}`}>
              <t.Icon size={18} /><span className="mt-0.5">{t.label}</span>
            </button>
          ))}
        </div>
        <div className="mt-4 pb-24">
          {active === "home"     && <HomeGrid setTab={setTab} />}
          {active === "scan"     && <ScanAndPay />}
          {active === "merchant" && <MerchantSection />}
          {active === "customer" && <CustomerSection />}
        </div>
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white px-4 pt-6 pb-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80">
          <ShieldCheck size={14} /> Find2Near
        </div>
        <div className="mt-2 flex items-center gap-2 text-3xl font-bold" data-testid="findpay-title">
          <Coins size={28} /> FindPay
        </div>
        <div className="text-sm opacity-90 mt-1">Pay · Collect · Transfer · Grow · Worldwide</div>
      </div>
    </div>
  );
}

function EarlyAccessGate() {
  return (
    <div className="min-h-screen grid place-items-center bg-stone-50 p-6">
      <div className="max-w-md text-center bg-white p-8 rounded-3xl shadow-md" data-testid="findpay-early-access">
        <Coins size={40} className="mx-auto text-emerald-600" />
        <h1 className="text-2xl font-bold mt-2">FindPay · Early Access</h1>
        <p className="text-sm text-stone-500 mt-2 leading-relaxed">
          FindPay is our universal payment layer for every Find2Near tab.
          It's currently in closed beta while we finish RBI/PCI compliance
          on live payment aggregators.
        </p>
        <div className="mt-6 text-left text-xs bg-stone-50 p-3 rounded-xl leading-relaxed">
          <div className="font-semibold mb-1">What's ready in preview:</div>
          <ul className="list-disc pl-4 space-y-1 text-stone-600">
            <li>Merchant onboarding + KYC (self-declared)</li>
            <li>Payment orchestration + state machine</li>
            <li>Universal fee ledger (transparent MDR + subsidy)</li>
            <li>UPI QR + Payment Links (mock provider)</li>
            <li>Razorpay + Cashfree adapters (activate with keys)</li>
          </ul>
        </div>
        <Link to="/" className="mt-6 inline-block text-sm text-emerald-700 font-semibold">← Back home</Link>
      </div>
    </div>
  );
}

function SignInGate() {
  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="text-center">
        <div className="text-lg font-semibold">Sign in to use FindPay</div>
        <Link to="/account" className="text-emerald-700 text-sm underline mt-2 inline-block">Go to sign-in →</Link>
      </div>
    </div>
  );
}

// ─── Home ────────────────────────────────────────────────────────────
function HomeGrid({ setTab }) {
  const [testCheckoutOpen, setTestOpen] = useState(false);
  const [testMerchant, setTestMerchant] = useState(null);
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/findpay/merchants/me").then(r => setMe(r.data)).catch(() => {}); }, []);

  const openTestCheckout = async () => {
    // Ensure the caller has a merchant profile, else prompt to make one.
    try {
      const { data } = await api.get("/findpay/merchants/me");
      setTestMerchant(data);
      setTestOpen(true);
    } catch {
      toast.info("Onboard a merchant first from the Merchant tab.");
      setTab("merchant");
    }
  };

  const actions = [
    { key: "scan",       label: "Scan & Pay",       Icon: ScanLine,  onClick: () => setTab("scan") },
    { key: "send",       label: "Send Money",       Icon: Send,      onClick: () => toast.info("Send Money launches when Razorpay Payouts keys are provided.") },
    { key: "request",    label: "Request Money",    Icon: HandCoins, onClick: () => toast.info("Request Money uses P2P UPI intent — coming with your bank VPA.") },
    { key: "merchant",   label: "Pay Merchant",     Icon: Store,     onClick: () => setTab("scan") },
    { key: "myqr",       label: "My QR",            Icon: QrCode,    onClick: () => setTab("merchant") },
    { key: "link",       label: "Payment Link",     Icon: Link2,     onClick: () => setTab("merchant") },
    { key: "history",    label: "History",          Icon: Receipt,   onClick: () => setTab("customer") },
    { key: "checkout",   label: "Test Checkout",    Icon: Sparkles,  onClick: openTestCheckout },
  ];

  return (
    <div>
      {me && (
        <Link to="/findpay?tab=merchant" className="block mb-3 p-3 bg-white rounded-xl shadow-sm border-l-4 border-emerald-500" data-testid="findpay-my-merchant-card">
          <div className="text-[10px] uppercase text-stone-400">Merchant profile</div>
          <div className="flex items-center justify-between mt-0.5">
            <div className="font-semibold text-stone-800">{me.business_name}</div>
            <Badge variant="secondary" className="text-[10px]">{me.tier_id}</Badge>
          </div>
          <div className="text-[11px] text-stone-500 mt-0.5">{me.category} · {me.country} · KYC {me.kyc_status}</div>
        </Link>
      )}
      <div className="grid grid-cols-4 gap-3">
        {actions.map(a => (
          <button key={a.key} onClick={a.onClick} data-testid={`findpay-action-${a.key}`}
            className="bg-white rounded-2xl p-3 shadow-sm hover:shadow-md transition text-center">
            <a.Icon size={26} className="mx-auto text-emerald-600" />
            <div className="text-[11px] font-semibold mt-1 text-stone-700 leading-tight">{a.label}</div>
          </button>
        ))}
      </div>

      <FeatureStrip />
      <TrustStrip />

      {testMerchant && (
        <FindPayCheckout
          open={testCheckoutOpen}
          onOpenChange={setTestOpen}
          merchantId={testMerchant.merchant_id}
          amountPaise={9900}
          currency={testMerchant.currency || "INR"}
          description="Test checkout · ₹99"
          onSuccess={() => toast.success("Test payment complete — see History")}
        />
      )}
    </div>
  );
}

function FeatureStrip() {
  return (
    <div className="mt-6 bg-white rounded-2xl p-4 shadow-sm">
      <div className="text-xs uppercase text-stone-500 tracking-widest mb-2">FindPay works with every tab</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
        {["Cab", "Hotel", "Flight", "Movie", "Train/Bus", "Property", "Shopping", "Charity"].map(x =>
          <div key={x} className="bg-stone-50 rounded-lg py-2 text-center text-stone-600">{x}</div>
        )}
      </div>
    </div>
  );
}

function TrustStrip() {
  return (
    <div className="mt-4 bg-emerald-50 rounded-2xl p-3 text-xs text-emerald-900 leading-relaxed">
      <div className="flex gap-2 items-start">
        <ShieldCheck size={16} className="shrink-0 mt-0.5" />
        <div>
          FindPay orchestrates payments through licensed partners
          (Razorpay / Cashfree). We do NOT store your card details, do
          NOT hold merchant funds, and every transaction is auditable.
        </div>
      </div>
    </div>
  );
}

// ─── Scan & Pay ──────────────────────────────────────────────────────
function ScanAndPay() {
  const [params] = useSearchParams();
  const [vpa, setVpa] = useState(params.get("vpa") || "");
  const [amount, setAmount] = useState(params.get("am") || "");
  const [note, setNote] = useState("");
  const submit = () => {
    if (!vpa.includes("@")) return toast.error("Enter a valid UPI VPA like ravi@upi");
    const amt = parseFloat(amount);
    if (!(amt > 0)) return toast.error("Enter a positive amount");
    const url = `upi://pay?pa=${encodeURIComponent(vpa)}&pn=Payee&am=${amt.toFixed(2)}&cu=INR&tn=${encodeURIComponent(note || "FindPay")}`;
    window.location.href = url;
  };
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="findpay-scan-panel">
      <div className="flex gap-2 items-center text-emerald-700 font-semibold">
        <ScanLine size={20} /> Scan or type UPI ID
      </div>
      <div className="text-xs text-stone-500 mt-1">
        Any UPI VPA works — from any bank, from any country supporting UPI-International.
      </div>
      <div className="mt-4 space-y-3">
        <Input value={vpa} onChange={e => setVpa(e.target.value)} placeholder="ravi@okhdfcbank" data-testid="findpay-scan-vpa" />
        <Input value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount (₹)" type="number" min="1" data-testid="findpay-scan-amount" />
        <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)" rows={2} data-testid="findpay-scan-note" />
        <Button onClick={submit} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="findpay-scan-submit">Open in UPI app</Button>
      </div>
      <div className="mt-4 text-[10px] text-stone-400 text-center leading-tight">
        FindPay redirects you to your UPI app. The amount is charged in ₹ INR;
        for cross-border cards, your issuer performs the FX at their published rate.
      </div>
    </div>
  );
}

// ─── Merchant ────────────────────────────────────────────────────────
function MerchantSection() {
  const [me, setMe] = useState(undefined);      // undefined = loading, null = missing
  const reload = () => api.get("/findpay/merchants/me").then(r => setMe(r.data)).catch(() => setMe(null));
  useEffect(() => { reload(); }, []);

  if (me === undefined) return <div className="p-8 text-center text-stone-400 text-sm">Loading…</div>;
  if (me === null) return <MerchantOnboard onCreated={reload} />;
  return <MerchantDashboard me={me} onUpdate={reload} />;
}

function MerchantOnboard({ onCreated }) {
  const [form, setForm] = useState({
    business_name: "", category: "retail", country: "IN", currency: "INR",
    gstin: "", pan: "", contact_email: "", contact_phone: "",
    address: "", upi_vpa: "", bank_account_number: "", bank_ifsc: "",
  });
  const [supportedCcy, setSupportedCcy] = useState(["INR"]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/findpay/currencies").then(r => setSupportedCcy(r.data?.currencies || ["INR"])).catch(() => {});
  }, []);
  const COUNTRY_CCY = { IN:"INR", US:"USD", GB:"GBP", AE:"AED", SG:"SGD", AU:"AUD", CA:"CAD", JP:"JPY", NP:"NPR", BT:"BTN", LK:"LKR", BD:"BDT", DE:"EUR", FR:"EUR", IT:"EUR", ES:"EUR" };
  const upd = (k, v) => {
    setForm(f => {
      const next = { ...f, [k]: v };
      // Auto-pick currency when country changes and user hasn't overridden.
      if (k === "country") {
        const c = String(v).toUpperCase();
        const suggested = COUNTRY_CCY[c];
        if (suggested) next.currency = suggested;
      }
      return next;
    });
  };
  const submit = async () => {
    if (!form.business_name.trim()) return toast.error("Business name required");
    setBusy(true);
    try {
      await api.post("/findpay/merchants/onboard", form);
      toast.success("Merchant profile created");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onboarding failed");
    } finally { setBusy(false); }
  };
  const isForeign = form.country !== "IN";
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="findpay-merchant-onboard">
      <div className="flex gap-2 items-center text-emerald-700 font-semibold">
        <Store size={20} /> Become a FindPay merchant
      </div>
      <div className="text-xs text-stone-500 mt-1">Self-declared onboarding — verify later via DigiLocker.</div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input placeholder="Business name*" value={form.business_name} onChange={e => upd("business_name", e.target.value)} data-testid="findpay-onb-name" />
        <Input placeholder="Category (retail / food / …)" value={form.category} onChange={e => upd("category", e.target.value)} />
        <Input placeholder="Country (IN / US / …)" value={form.country} onChange={e => upd("country", e.target.value.toUpperCase())} data-testid="findpay-onb-country" maxLength={2} />
        <select
          value={form.currency}
          onChange={e => upd("currency", e.target.value)}
          className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
          data-testid="findpay-onb-currency"
        >
          {supportedCcy.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <Input placeholder="GSTIN (optional)" value={form.gstin} onChange={e => upd("gstin", e.target.value)} />
        <Input placeholder="PAN (optional)" value={form.pan} onChange={e => upd("pan", e.target.value)} />
        <Input placeholder="UPI VPA (e.g., biz@okaxis)" value={form.upi_vpa} onChange={e => upd("upi_vpa", e.target.value)} data-testid="findpay-onb-vpa" />
        <Input placeholder="Bank A/C number" value={form.bank_account_number} onChange={e => upd("bank_account_number", e.target.value)} />
        <Input placeholder="IFSC" value={form.bank_ifsc} onChange={e => upd("bank_ifsc", e.target.value)} />
        <Input placeholder="Contact email" value={form.contact_email} onChange={e => upd("contact_email", e.target.value)} />
        <Input placeholder="Contact phone" value={form.contact_phone} onChange={e => upd("contact_phone", e.target.value)} />
        <Input placeholder="Address" value={form.address} onChange={e => upd("address", e.target.value)} className="sm:col-span-2" />
      </div>
      {isForeign && (
        <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-[11px] text-amber-900 leading-relaxed" data-testid="findpay-foreign-notice">
          <b>Global merchant onboarding · beta.</b> You've selected a non-Indian country.
          FindPay will collect payments in {form.currency}. <b>Cross-border payouts</b> require
          a licensed AD-II / international acquirer — we're onboarding a partner in Q2 2026.
          Until then, foreign merchants can (a) accept UPI-International from Indian NRIs and settle
          via a partnered Indian PA, or (b) receive an INR payout to a resident bank account.
        </div>
      )}
      <Button onClick={submit} disabled={busy} className="mt-4 w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="findpay-onb-submit">
        {busy ? "Creating…" : "Create merchant profile"}
      </Button>
    </div>
  );
}

function MerchantDashboard({ me, onUpdate }) {
  const [dash, setDash] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [showQR, setShowQR]     = useState(false);
  const [showPoster, setShowPoster] = useState(false);
  const [lastLink, setLastLink] = useState(null);
  const [lastQR, setLastQR]     = useState(null);
  const load = () => api.get("/findpay/merchants/dashboard").then(r => setDash(r.data));
  useEffect(() => { load(); }, []);

  const verifyDL = async () => { setBusy(true); try { await api.post("/findpay/merchants/kyc/digilocker"); toast.success("Verified (mock DigiLocker)"); onUpdate?.(); } finally { setBusy(false); } };
  const currency = me.currency || "INR";
  const t = dash?.totals || {};

  return (
    <div>
      <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="findpay-merchant-dashboard">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-lg font-bold text-stone-800">{me.business_name}</div>
            <div className="text-xs text-stone-500 mt-0.5">{me.category} · {me.country} · Tier <b>{me.tier_id}</b></div>
          </div>
          <Badge variant={me.kyc_status === "verified" ? "default" : "secondary"} className={me.kyc_status === "verified" ? "bg-emerald-600" : ""}>{me.kyc_status}</Badge>
        </div>
        {me.kyc_status !== "verified" && (
          <Button onClick={verifyDL} disabled={busy} variant="outline" className="mt-3 h-9 text-xs" data-testid="findpay-verify-digilocker">
            Verify via DigiLocker (mock)
          </Button>
        )}

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Collected (30d)"  value={paiseToStr(t.total_paid_paise, currency)} tone="green" />
          <Stat label="Pending"          value={paiseToStr(t.pending_paise, currency)} />
          <Stat label="Refunded"         value={paiseToStr(t.refunded_paise, currency)} tone="red" />
          <Stat label="You receive"      value={paiseToStr(t.net_receivable_paise, currency)} tone="green" />
        </div>

        <div className="mt-3 border-t pt-3 text-xs text-stone-600 space-y-1">
          <div className="flex justify-between"><span>Payment cost absorbed by you</span><span className="font-semibold">{paiseToStr(t.merchant_mdr_paise, currency)}</span></div>
          <div className="flex justify-between"><span>Find2Near subsidy</span><span className="text-emerald-700 font-semibold">- {paiseToStr(t.subsidy_paise, currency)}</span></div>
          <div className="flex justify-between"><span>Platform fee</span><span>{paiseToStr(t.platform_fee_paise, currency)}</span></div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button onClick={() => setShowQR(true)}   variant="outline" className="h-11" data-testid="findpay-new-qr"><QrCode size={16} className="mr-2" />New QR</Button>
          <Button onClick={() => setShowLink(true)} variant="outline" className="h-11" data-testid="findpay-new-link"><Link2 size={16} className="mr-2" />New Payment Link</Button>
          <Button onClick={() => setShowPoster(true)} variant="outline" className="h-11 col-span-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="findpay-poster"><Printer size={16} className="mr-2" />Growth Kit · Poster + WhatsApp share</Button>
        </div>
      </div>

      {showPoster && <PosterModal me={me} onClose={() => setShowPoster(false)} />}

      {showQR && <QRDrawer me={me} onClose={() => setShowQR(false)} onCreated={setLastQR} />}
      {showLink && <LinkDrawer me={me} onClose={() => setShowLink(false)} onCreated={setLastLink} />}
      {lastQR   && <ResultCard title="Merchant QR" data={lastQR.upi_intent_url} note="Print or share this UPI QR." />}
      {lastLink && <ResultCard title="Payment Link" data={lastLink.url} note={`Amount: ${paiseToStr(lastLink.amount_paise, lastLink.currency)}`} />}

      <div className="mt-5">
        <div className="text-xs uppercase text-stone-500 tracking-widest mb-2">Recent transactions</div>
        <TxnList items={dash?.recent} currency={currency} onRefund={async (txnId) => {
          try { await api.post("/findpay/refunds", { txn_id: txnId }); toast.success("Refund initiated"); load(); }
          catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
        }} />
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const tones = { green: "text-emerald-700", red: "text-red-600" };
  return (
    <div className="bg-stone-50 rounded-xl p-3">
      <div className="text-[10px] uppercase text-stone-500 tracking-widest">{label}</div>
      <div className={`text-sm font-bold mt-0.5 ${tones[tone] || "text-stone-800"}`}>{value}</div>
    </div>
  );
}

function QRDrawer({ me, onClose, onCreated }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/findpay/qr", {
        amount_paise: amount ? Math.round(parseFloat(amount) * 100) : undefined,
        description: note,
      });
      onCreated(data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 grid place-items-center" onClick={onClose}>
      <div className="bg-white p-5 rounded-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="font-semibold">New QR for {me.business_name}</div>
        <div className="mt-3 space-y-2">
          <Input placeholder="Amount (optional)" type="number" value={amount} onChange={e => setAmount(e.target.value)} data-testid="findpay-qr-amount" />
          <Input placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} />
        </div>
        <Button onClick={submit} disabled={busy} className="mt-3 w-full h-10 bg-emerald-600 hover:bg-emerald-700" data-testid="findpay-qr-create">Create QR</Button>
      </div>
    </div>
  );
}

function LinkDrawer({ me, onClose, onCreated }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!(amt > 0)) return toast.error("Enter an amount");
    setBusy(true);
    try {
      const { data } = await api.post("/findpay/links", {
        amount_paise: Math.round(amt * 100),
        description: note, customer_email: email,
      });
      onCreated(data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 grid place-items-center" onClick={onClose}>
      <div className="bg-white p-5 rounded-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="font-semibold">New Payment Link</div>
        <div className="mt-3 space-y-2">
          <Input placeholder="Amount*" type="number" value={amount} onChange={e => setAmount(e.target.value)} data-testid="findpay-link-amount" />
          <Input placeholder="Customer email (optional)" value={email} onChange={e => setEmail(e.target.value)} />
          <Input placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} />
        </div>
        <Button onClick={submit} disabled={busy} className="mt-3 w-full h-10 bg-emerald-600 hover:bg-emerald-700" data-testid="findpay-link-create">Create Link</Button>
      </div>
    </div>
  );
}

function ResultCard({ title, data, note }) {
  const copy = () => { navigator.clipboard.writeText(data); toast.success("Copied"); };
  return (
    <div className="mt-3 bg-white rounded-2xl p-4 shadow-sm" data-testid="findpay-result">
      <div className="text-xs uppercase text-stone-500 tracking-widest">{title}</div>
      <div className="mt-1 font-mono text-xs break-all bg-stone-50 p-2 rounded" data-testid="findpay-result-data">{data}</div>
      <div className="mt-1 text-[11px] text-stone-500">{note}</div>
      <Button variant="outline" onClick={copy} className="mt-2 h-8 text-xs">Copy</Button>
    </div>
  );
}

function TxnList({ items, currency, onRefund }) {
  const list = items || [];
  if (!list.length) return <div className="bg-white rounded-2xl p-6 text-center text-stone-400 text-sm">No transactions yet.</div>;
  return (
    <div className="bg-white rounded-2xl divide-y overflow-hidden">
      {list.map(t => (
        <div key={t.txn_id} className="p-3 flex items-center gap-3 text-sm" data-testid={`findpay-txn-${t.txn_id}`}>
          <div className={`h-2 w-2 rounded-full ${t.state === "SUCCESS" || t.state === "SETTLED" ? "bg-emerald-500" : t.state === "REFUNDED" ? "bg-orange-500" : t.state === "FAILED" ? "bg-red-500" : "bg-stone-300"}`}></div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-stone-800 truncate">{t.description || t.txn_id}</div>
            <div className="text-[11px] text-stone-500">{t.state} · {t.rail} · {new Date(t.created_at).toLocaleString()}</div>
          </div>
          <div className="text-right">
            <div className="font-bold">{paiseToStr(t.amount_paise, t.currency || currency)}</div>
            {onRefund && (t.state === "SUCCESS" || t.state === "SETTLED") && (
              <button onClick={() => onRefund(t.txn_id)} className="text-[11px] text-red-500 underline mt-0.5" data-testid={`findpay-refund-${t.txn_id}`}>Refund</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Growth Kit · printable poster + WhatsApp share ──────────────────
function PosterModal({ me, onClose }) {
  const canvasRef = useRef(null);
  const upiUrl = useMemo(() => {
    const vpa = me.upi_vpa || "";
    if (!vpa) return "";
    return `upi://pay?pa=${encodeURIComponent(vpa)}&pn=${encodeURIComponent(me.business_name)}&cu=${me.currency || "INR"}&tn=${encodeURIComponent("FindPay · " + me.business_name)}`;
  }, [me]);

  // Server-independent PDF via jsPDF-style browser Print. We keep it simple:
  // render a hi-res <canvas>, download as PNG, and also expose a Print dialog
  // that prints the same rendered area A4-sized (browser handles the PDF export).
  const drawPoster = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = 1000, H = 1414;                       // ~A4 @ 120dpi
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");
    // Background
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, "#065f46"); grad.addColorStop(1, "#0f766e");
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
    // White card
    ctx.fillStyle = "#fff"; ctx.fillRect(60, 240, W - 120, H - 380);
    // FindPay header
    ctx.fillStyle = "#fff";
    ctx.textAlign = "center";
    ctx.font = "bold 60px system-ui, sans-serif";
    ctx.fillText("FindPay", W / 2, 130);
    ctx.font = "22px system-ui, sans-serif";
    ctx.fillText("Scan · Pay · Instant", W / 2, 175);
    // Business title
    ctx.fillStyle = "#0f172a";
    ctx.font = "bold 56px system-ui, sans-serif";
    wrapText(ctx, me.business_name || "Merchant", W / 2, 340, W - 200, 62);
    // Category
    ctx.fillStyle = "#64748b";
    ctx.font = "26px system-ui, sans-serif";
    ctx.fillText(`${me.category || "General"} · ${me.city || me.country || ""}`, W / 2, 430);
    // VPA / UPI line
    ctx.fillStyle = "#0f5132";
    ctx.font = "bold 36px monospace";
    ctx.fillText(me.upi_vpa || "no-vpa@configure-in-app", W / 2, 500);
    // Footer note
    ctx.fillStyle = "#475569";
    ctx.font = "24px system-ui, sans-serif";
    ctx.fillText("Any UPI app · Any bank · Any country", W / 2, H - 260);
    ctx.font = "18px system-ui, sans-serif";
    ctx.fillText("Powered by Find2Near — find2near.com", W / 2, H - 220);
    // QR is drawn separately (see JSX below) and composited onto the canvas.
    const qrEl = document.getElementById("findpay-poster-qr");
    if (qrEl && qrEl.tagName === "CANVAS") {
      const qrSize = 480;
      ctx.drawImage(qrEl, (W - qrSize) / 2, 560, qrSize, qrSize);
    }
  };

  useEffect(() => {
    const t = setTimeout(drawPoster, 100);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [me]);

  const downloadPNG = () => {
    drawPoster();
    const canvas = canvasRef.current;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url; a.download = `findpay-poster-${me.business_name.replace(/\s+/g, "-").toLowerCase()}.png`;
    a.click();
  };

  const printPoster = () => {
    drawPoster();
    const dataUrl = canvasRef.current.toDataURL("image/png");
    const w = window.open("", "_blank");
    if (!w) { toast.error("Enable pop-ups to print."); return; }
    w.document.write(`<html><head><title>FindPay poster · ${me.business_name}</title>
      <style>body{margin:0;padding:0;text-align:center;} img{width:100%;max-width:900px;} @media print{@page{margin:8mm;}}</style>
      </head><body onload="window.print();window.close();"><img src="${dataUrl}" /></body></html>`);
    w.document.close();
  };

  const shareWhatsApp = () => {
    const msg = encodeURIComponent(
      `Pay ${me.business_name} on FindPay\n` +
      (me.upi_vpa ? `UPI: ${me.upi_vpa}\n` : "") +
      `Open: ${window.location.origin}/findpay?tab=scan${me.upi_vpa ? `&vpa=${encodeURIComponent(me.upi_vpa)}` : ""}\n\n` +
      `Powered by Find2Near`,
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="findpay-poster-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between">
          <div className="font-semibold text-stone-800">Growth Kit · Poster</div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600 text-sm">✕</button>
        </div>
        <div className="p-4">
          <div className="text-xs text-stone-500 mb-2">
            Print this A4 poster for your shop or send the WhatsApp message directly to customers.
          </div>
          <div className="bg-stone-50 rounded-xl p-3 grid place-items-center">
            <canvas ref={canvasRef} className="w-full max-w-[280px] h-auto shadow rounded" data-testid="findpay-poster-canvas" />
            {upiUrl && (
              <div style={{ position: "absolute", left: -9999 }}>
                {/* Hidden QR canvas used as a source for compositing. */}
                <QRCodeCanvas id="findpay-poster-qr" value={upiUrl} size={480} includeMargin={true} level="H" />
              </div>
            )}
          </div>
          {!me.upi_vpa && (
            <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 p-2 rounded">
              Add a UPI VPA in your merchant profile so the QR encodes a real payment intent.
            </div>
          )}
          <div className="mt-3 grid grid-cols-3 gap-2">
            <Button onClick={downloadPNG}    variant="outline" className="h-10" data-testid="findpay-poster-png"><Download size={14} className="mr-1"/>PNG</Button>
            <Button onClick={printPoster}    variant="outline" className="h-10" data-testid="findpay-poster-pdf"><Printer size={14} className="mr-1"/>Print / PDF</Button>
            <Button onClick={shareWhatsApp}  className="h-10 bg-emerald-600 hover:bg-emerald-700"     data-testid="findpay-poster-whatsapp"><MessageCircleMore size={14} className="mr-1"/>WhatsApp</Button>
          </div>
          <div className="text-[10px] text-stone-400 mt-2 leading-tight text-center">
            The WhatsApp button opens wa.me with a pre-filled message. On desktop, WhatsApp Web will ask you to pick a contact.
          </div>
        </div>
      </div>
    </div>
  );
}

// Small helper: word-wrap text on a canvas.
function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text).split(" ");
  let line = "";
  for (const w of words) {
    const test = line + w + " ";
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, y); line = w + " "; y += lineHeight;
    } else { line = test; }
  }
  ctx.fillText(line, x, y);
}

// ─── Customer ────────────────────────────────────────────────────────
function CustomerSection() {
  const [dash, setDash] = useState(null);
  const load = () => api.get("/findpay/customer/dashboard").then(r => setDash(r.data));
  useEffect(() => { load(); }, []);
  if (!dash) return <div className="p-8 text-center text-stone-400 text-sm">Loading…</div>;
  const t = dash.totals;
  return (
    <div>
      <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="findpay-customer-dashboard">
        <div className="text-lg font-bold text-stone-800">Your FindPay</div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          <Stat label="Spent (30d)" value={paiseToStr(t.spent_paise)} tone="green" />
          <Stat label="Refunded"    value={paiseToStr(t.refunded_paise)} tone="red" />
          <Stat label="Payments"    value={String(t.txn_count)} />
        </div>
      </div>
      <div className="mt-4">
        <div className="text-xs uppercase text-stone-500 tracking-widest mb-2">Recent</div>
        <TxnList items={dash.recent} />
      </div>
    </div>
  );
}
